import { useProfile } from "@/context/useProfileProps";
import { Button, Edit, OTPInput } from "@monsterindia/global-ui-components";
import React, { useEffect, useImperativeHandle, useState } from "react";

import {
  useSendMobileOTP,
  useVerifyMobileOTP,
} from "@/components/profile/contactCard/query";
import { MobileOTPProps } from "@/components/profile/contactCard/types";

const MobileOTP = ({
  closeMobileModal,
  mobileNumber,
  countryCode,
  otpId,
  changeMobileNumberType,
  otpRef,
  setLoading,
  executeRecaptcha,
}: MobileOTPProps) => {
  const {
    translation: { contactCard, errorMessages },
    showToast,
    handleEventTracking,
  } = useProfile();
  const [mobileOTPId, setMobileOTPId] = useState(otpId);
  const [otpValue, setOTPValue] = useState("");
  const [otpError, setOtpError] = useState<string>("");

  const { mutate: sendMobileOTP } = useSendMobileOTP();
  const { mutate: verifyMobileOTP } = useVerifyMobileOTP();

  const otpUpdateHandler = (newOTP: string) => {
    setOTPValue(newOTP);
    setOtpError("");
  };

  useEffect(() => {
    setMobileOTPId(otpId);
  }, [otpId]);

  useImperativeHandle(otpRef, () => ({
    verifyOTPHandler: () =>
      verifyMobileOTPHandler(otpValue, mobileOTPId, mobileNumber, countryCode),
  }));

  const sendMobileOTPHandler = async (
    mobileNumber: string,
    countryCode: string,
  ) => {
    setLoading(true);
    const { reCaptchaToken, reCaptchaAction } =
      await executeRecaptcha("sendotp");

    const apiData = {
      username: `+${countryCode}${mobileNumber}`,
      reCaptchaToken,
      reCaptchaAction,
    };

    // api call to send mobile otp
    sendMobileOTP(apiData, {
      onSuccess: (data) => {
        setMobileOTPId(data?.id);
        setLoading(false);
        showToast({
          show: true,
          type: "success",
          message: "OTP sent successfully",
        });
      },
      onError: (err) => {
        setLoading(false);
        showToast({
          show: true,
          type: "error",
          message:
            err instanceof Error
              ? err.message
              : errorMessages.somethingWentWrong,
        });
      },
    });
  };

  const verifyMobileOTPHandler = async (
    otpValue: string,
    otpId: string,
    mobileNumber: string,
    countryCode: string,
  ) => {
    setLoading(true);
    const apiData = {
      mobileNumber: {
        mobileNumber: mobileNumber,
        countryCode: countryCode,
      },
      otp: otpValue,
      otpId: otpId,
    };
    setOtpError("");
    verifyMobileOTP(apiData, {
      onSuccess: (data) => {
        setLoading(false);
        showToast({
          show: true,
          type: "success",
          message: "OTP verified successfully",
        });
        let urlParams = new URLSearchParams(window.location.search);
        const isApp = urlParams.get("app") === "true";
        handleEventTracking({
          eventType: "eventPush",
          eventName: "ProfilePage_Section_Verified",
          eventProps: {
            Contact_Info_Verified: `phone number = +${countryCode} ${mobileNumber}`,
            Source: isApp ? "app" : "web",
          },
        });
        closeMobileModal("save");
      },
      onError: (err) => {
        setLoading(false);
        setOtpError(
          err instanceof Error ? err.message : errorMessages.somethingWentWrong,
        );
        // showToast({
        //   show: true,
        //   type: "error",
        //   message:
        //     err instanceof Error
        //       ? err.message
        //       : errorMessages.somethingWentWrong,
        // });
      },
    });
  };

  const handleResendClick = () => {
    sendMobileOTPHandler(mobileNumber, countryCode);
    setOTPValue("");
    setOtpError("");
  };

  return (
    <div className="flex w-full flex-col gap-4 md:max-h-[370px] md:min-h-[40vh]">
      <div className="flex flex-col items-start gap-0 text-sm">
        <p className="text-grey-3 text-sm">
          {contactCard.sendVerificationCodeHeadingForMobile}
        </p>
        <Button
          type="button"
          className="!w-max !px-0 !font-normal !text-blue-600"
          modifier="tertiary"
          rightIcon={<Edit width={16} height={16} />}
          label={`+${countryCode}${mobileNumber}`}
          onClick={() => changeMobileNumberType()}
        />
      </div>

      <OTPInput
        otpValue={otpValue}
        errorMessage={otpError}
        otpUpdateHandler={otpUpdateHandler}
        resendClickHandler={handleResendClick}
        resendTime={60}
      />
    </div>
  );
};

export default MobileOTP;
